/**
 * Date Timezone Conversion Utilities
 * Handles conversion between user timezone and school timezone for attendance dates
 */

import { getCurrentTimezoneConfig, getResolvedTimezone } from './timezoneUtils';

/**
 * Convert a date from user's timezone to school's timezone
 * This ensures that when a user in Tokyo submits a report for "9/7", 
 * a school in NY timezone will see it as "9/6" if that's what the actual date should be
 * 
 * @param {string} dateString - Date in YYYY-MM-DD format from user
 * @param {string} schoolTimezone - School's timezone (e.g., 'America/New_York')
 * @param {string} userTimezone - User's timezone (optional, will use user's setting if not provided)
 * @returns {string} - Date in YYYY-MM-DD format adjusted for school timezone
 */
export const convertDateFromUserToSchoolTimezone = (dateString, schoolTimezone, userTimezone = null) => {
  if (!dateString || !schoolTimezone) {
    console.warn('Missing required parameters for date conversion:', { dateString, schoolTimezone });
    return dateString;
  }

  try {
    // Get user's timezone (from Account Settings or parameter)
    const inputTimezone = userTimezone || getResolvedTimezone();
    
    // If user is using LOCAL timezone or same as school, no conversion needed
    if (!inputTimezone || inputTimezone === schoolTimezone || getCurrentTimezoneConfig() === 'LOCAL') {
      return dateString;
    }

    console.log('🗓️ Converting date from user timezone to school timezone:', {
      originalDate: dateString,
      userTimezone: inputTimezone,
      schoolTimezone: schoolTimezone
    });

    // Parse the date string (YYYY-MM-DD)
    const [year, month, day] = dateString.split('-').map(Number);
    
    // Create a date at noon in the user's timezone to avoid edge cases with midnight
    const userDate = new Date();
    userDate.setFullYear(year, month - 1, day);
    userDate.setHours(12, 0, 0, 0); // Set to noon to avoid DST issues

    // Get the date components in the user's timezone
    const userDateInUserTz = new Date(userDate.toLocaleString('en-US', { timeZone: inputTimezone }));
    
    // Get what this same moment looks like in the school's timezone
    const dateInSchoolTz = new Date(userDate.toLocaleString('en-US', { timeZone: schoolTimezone }));
    
    // Calculate the offset difference in milliseconds
    const offsetDifference = dateInSchoolTz.getTime() - userDateInUserTz.getTime();
    
    // Apply the offset to get the school's date
    const schoolDate = new Date(userDate.getTime() + offsetDifference);
    
    // Format as YYYY-MM-DD
    const schoolDateString = schoolDate.toLocaleDateString('en-CA'); // en-CA gives us YYYY-MM-DD format
    
    console.log('🗓️ Date conversion result:', {
      originalDate: dateString,
      convertedDate: schoolDateString,
      userTimezone: inputTimezone,
      schoolTimezone: schoolTimezone
    });

    return schoolDateString;

  } catch (error) {
    console.error('❌ Error converting date timezone:', error);
    return dateString; // Fallback to original date
  }
};

/**
 * Convert a date from school's timezone to user's timezone for display
 * This is the reverse operation - for showing school dates in user's timezone
 * 
 * @param {string} dateString - Date in YYYY-MM-DD format from school timezone
 * @param {string} schoolTimezone - School's timezone (e.g., 'America/New_York')
 * @param {string} userTimezone - User's timezone (optional, will use user's setting if not provided)
 * @returns {string} - Date in YYYY-MM-DD format adjusted for user timezone
 */
export const convertDateFromSchoolToUserTimezone = (dateString, schoolTimezone, userTimezone = null) => {
  if (!dateString || !schoolTimezone) {
    console.warn('Missing required parameters for date conversion:', { dateString, schoolTimezone });
    return dateString;
  }

  try {
    // Get user's timezone (from Account Settings or parameter)
    const displayTimezone = userTimezone || getResolvedTimezone();
    
    // If user is using LOCAL timezone or same as school, no conversion needed
    if (!displayTimezone || displayTimezone === schoolTimezone || getCurrentTimezoneConfig() === 'LOCAL') {
      return dateString;
    }

    console.log('🗓️ Converting date from school timezone to user timezone:', {
      originalDate: dateString,
      schoolTimezone: schoolTimezone,
      userTimezone: displayTimezone
    });

    // Parse the date string (YYYY-MM-DD)
    const [year, month, day] = dateString.split('-').map(Number);
    
    // Create a date at noon in the school's timezone
    const schoolDate = new Date();
    schoolDate.setFullYear(year, month - 1, day);
    schoolDate.setHours(12, 0, 0, 0); // Set to noon to avoid DST issues

    // Get the date components in the school's timezone
    const schoolDateInSchoolTz = new Date(schoolDate.toLocaleString('en-US', { timeZone: schoolTimezone }));
    
    // Get what this same moment looks like in the user's timezone
    const dateInUserTz = new Date(schoolDate.toLocaleString('en-US', { timeZone: displayTimezone }));
    
    // Calculate the offset difference in milliseconds
    const offsetDifference = dateInUserTz.getTime() - schoolDateInSchoolTz.getTime();
    
    // Apply the offset to get the user's date
    const userDate = new Date(schoolDate.getTime() + offsetDifference);
    
    // Format as YYYY-MM-DD
    const userDateString = userDate.toLocaleDateString('en-CA'); // en-CA gives us YYYY-MM-DD format
    
    console.log('🗓️ Date conversion result:', {
      originalDate: dateString,
      convertedDate: userDateString,
      schoolTimezone: schoolTimezone,
      userTimezone: displayTimezone
    });

    return userDateString;

  } catch (error) {
    console.error('❌ Error converting date timezone:', error);
    return dateString; // Fallback to original date
  }
};

/**
 * Get today's date in school timezone (for backend submission)
 * This should be used when we need to determine what "today" means for the school
 * 
 * @param {string} schoolTimezone - School's timezone (e.g., 'America/New_York')
 * @returns {string} - Today's date in YYYY-MM-DD format in school timezone
 */
export const getTodayInSchoolTimezone = (schoolTimezone) => {
  if (!schoolTimezone) {
    // Fallback to local date
    return new Date().toLocaleDateString('en-CA');
  }

  try {
    // Get current time in school's timezone
    const now = new Date();
    const schoolDate = new Date(now.toLocaleString('en-US', { timeZone: schoolTimezone }));
    
    return schoolDate.toLocaleDateString('en-CA'); // YYYY-MM-DD format
  } catch (error) {
    console.error('❌ Error getting today in school timezone:', error);
    return new Date().toLocaleDateString('en-CA'); // Fallback
  }
};

/**
 * Check if two dates represent the same day when accounting for timezone differences
 * 
 * @param {string} userDate - Date from user's perspective (YYYY-MM-DD)
 * @param {string} schoolDate - Date from school's perspective (YYYY-MM-DD)
 * @param {string} schoolTimezone - School's timezone
 * @param {string} userTimezone - User's timezone (optional)
 * @returns {boolean} - True if they represent the same logical day
 */
export const isSameLogicalDay = (userDate, schoolDate, schoolTimezone, userTimezone = null) => {
  const convertedUserDate = convertDateFromUserToSchoolTimezone(userDate, schoolTimezone, userTimezone);
  return convertedUserDate === schoolDate;
};
